import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Stack,
  Box,
  Container,
  useTheme,
  Snackbar,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  AlertTitle
} from '@mui/material';
import { format, isValid, parseISO, addDays } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../shared/logger';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import RepeatIcon from '@mui/icons-material/Repeat';
import './styles/MemberEvents.css';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import { EventParticipant as ApiEventParticipant, EventData as ApiEventData } from '../api_types';
import { memberApi, recurringEventApi, eventApi } from '../api';
import CreditBalance from '../components/CreditBalance';
import EventRefund from '../components/EventRefund';
import { eventRegistrationApi } from '../api';

interface Participant {
  userId: string;
  signUpDate: string;
  name?: string;
  participantId?: string;
}

interface EventData extends ApiEventData {
  participants?: Participant[];
  isCurrentUserSignedUp?: boolean;
  isRecurring?: boolean;
  recurringEventId?: string;
  recurrenceRule?: {
    frequency: string;
    interval?: number;
    endDate?: string;
    daysOfWeek?: number[];
  };
  isBlocked?: boolean;
}

interface MemberEventsProps {
  businessId: string;
  facilityId?: string;
}

interface EmptyStateProps {
  message: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ message }) => (
  <Box
    sx={{
      py: 6,
      textAlign: 'center',
      backgroundColor: '#f5f5f5',
      borderRadius: 2,
    }}
  >
    <Typography
      variant="body1"
      sx={{
        color: '#4f566b',
        fontSize: '14px',
      }}
    >
      {message}
    </Typography>
  </Box>
); 

const MemberEvents: React.FC<MemberEventsProps> = ({ businessId, facilityId }) => {
  const theme = useTheme();
  const [events, setEvents] = useState<EventData[]>([]);
  const [recurringEventCards, setRecurringEventCards] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [recurringEventsLoading, setRecurringEventsLoading] = useState(true);
  const [confirmUnsignup, setConfirmUnsignup] = useState<{open: boolean; eventId: string} | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [notification, setNotification] = useState<{message: string; severity: 'success' | 'error' | 'info'} | null>(null);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [showPaymentDialog, setShowPaymentDialog] = useState<{open: boolean; event: EventData | null}>({open: false, event: null});
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'credits'>('card');
  const { user } = useAuth();

  useEffect(() => {
    const fetchUserFacility = async () => {      
      if (!user) return;
      
      if (!businessId || !facilityId) {
        setError('No business or facility ID found');
        setLoading(false);
        return;
      }

      try {
        await memberApi.getMemberData(user.uid, businessId, facilityId);
      } catch (err) {
        logger.error('Error fetching user facility:', err instanceof Error ? err : new Error(String(err)));
      }
    };

    fetchUserFacility();
  }, [user, businessId, facilityId]);

  // Update the fetchCreditBalance function
  useEffect(() => {
    const fetchCreditBalance = async () => {
      if (!user || !facilityId || !businessId) return;
      
      try {
        const data = await eventRegistrationApi.getMemberCredits(user.uid, businessId, facilityId);
        setCreditBalance(data.balance);
      } catch (err) {
        console.error('Error fetching credit balance:', err);
      }
    };

    fetchCreditBalance();
  }, [user, facilityId, businessId]);

  // Safe date formatting helper
  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      if (!isValid(date)) return 'Invalid date';
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Invalid date';
    }
  };

  // Safe time formatting helper
  const formatTime = (timeStr: string) => {
    try {
      const date = parseISO(`2000-01-01T${timeStr}`);
      if (!isValid(date)) return timeStr;
      return format(date, 'h:mm a');
    } catch (e) {
      console.error('Error formatting time:', e);
      return timeStr;
    }
  };

  const fetchRecurringEvents = useCallback(async () => {
    if (!facilityId) return;

    try {
      setRecurringEventsLoading(true);
      const parentRecurringEvents = await recurringEventApi.getRecurringEvents(businessId, facilityId);
      const eventMap = new Map();
      parentRecurringEvents.forEach(event => {
        eventMap.set(event.id, event);
      });
      
      const dates: string[] = [];
      let currentDate = new Date();
      const endDate = addDays(new Date(), 30);
      
      while (currentDate <= endDate) {
        dates.push(format(currentDate, 'yyyy-MM-dd'));
        currentDate = addDays(currentDate, 1);
      }
      
      // Fetch occurrences for each date and collect them
      const allOccurrences: any[] = [];
      await Promise.all(
        dates.map(async (date) => {
          try {
            const dateOccurrences = await recurringEventApi.getRecurringEventOccurrencesByDate(businessId, facilityId, date);
            allOccurrences.push(...dateOccurrences);
          } catch (err) {
            logger.error(`Error fetching occurrences for date ${date}:`, { error: err instanceof Error ? err.message : String(err) });
          }
        })
      );
      
      // Group occurrences by parentEventId
      const occurrencesByParentId = new Map<string, any[]>();
      
      allOccurrences.forEach(occurrence => {
        if (!occurrencesByParentId.has(occurrence.parentEventId)) {
          occurrencesByParentId.set(occurrence.parentEventId, []);
        }
        occurrencesByParentId.get(occurrence.parentEventId)?.push(occurrence);
      });
      
      // Process events with their occurrences
      const events: EventData[] = await Promise.all(
        Array.from(occurrencesByParentId.entries()).map(async ([parentId, occurrences]) => {
          // Get the parent event data
          const parentEvent = eventMap.get(parentId);
          
          if (!parentEvent) {
            logger.error(`Parent event ${parentId} not found`);
            return null;
          }
          
          // Get participant details from the API
          const participants: Participant[] = [];
          try {
            const participantsRef = await recurringEventApi.getRecurringEvents(businessId, facilityId);
            const eventParticipants = participantsRef.find(e => e.id === parentId)?.participants || [];
            
            // Get user details for each participant
            const participantsWithNames = await Promise.all(eventParticipants.map(async (p: ApiEventParticipant) => {
              try {
                const userData = await memberApi.getMemberData(p.userId, businessId, facilityId);
                return {
                  ...p,
                  name: `${userData.firstName} ${userData.lastName}`
                };
              } catch (err) {
                logger.error('Error fetching participant details', { error: err instanceof Error ? err.message : String(err) });
                return {
                  ...p,
                  name: 'Unknown User'
                };
              }
            }));
            participants.push(...participantsWithNames);
          } catch (err) {
            logger.error('Error fetching participants', { error: err instanceof Error ? err.message : String(err) });
          }
          
          // Use the first occurrence for display
          const firstOccurrence = occurrences[0];
          
          return {
            ...parentEvent,
            date: firstOccurrence.date, // Use the first occurrence date for display
            isRecurring: true,
            recurringEventId: parentId,
            capacity: parentEvent.capacity || 0,
            participants,
            isCurrentUserSignedUp: user ? participants.some(p => p.userId === user.uid) : false,
            // Add occurrences data
            occurrences: occurrences
          };
        })
      );
      
      // Filter out null values and set state
      setRecurringEventCards(events.filter(Boolean));
    } catch (err) {
      logger.error('Failed to fetch recurring events', { error: err instanceof Error ? err.message : String(err) });
      setError('Failed to fetch recurring events');
    } finally {
      setRecurringEventsLoading(false);
    }
  }, [facilityId, user, businessId]);

  const fetchEvents = useCallback(async () => {
    if (!facilityId) return;

    try {
      setLoading(true);
      const apiEvents = await eventApi.getFacilityEvents(businessId, facilityId);
      const eventsData: EventData[] = await Promise.all(apiEvents.map(async event => {
        // Get participant details
        const participantsWithNames = await Promise.all((event.participants || []).map(async (p) => {
          try {
            const userData = await memberApi.getMemberData(p.userId, businessId, facilityId);
            return {
              ...p,
              name: `${userData.firstName} ${userData.lastName}`
            };
          } catch (err) {
            logger.error('Error fetching participant details', { error: err instanceof Error ? err.message : String(err) });
            return {
              ...p,
              name: 'Unknown User'
            };
          }
        }));

        return {
          ...event,
          capacity: event.capacity || 0,
          participants: participantsWithNames,
          isCurrentUserSignedUp: user ? participantsWithNames.some(p => p.userId === user.uid) : false
        };
      }));
      
      // Filter out past events and sort by date
      const now = new Date();
      
      const futureEvents = eventsData.filter(event => {
        try {
          const eventDate = parseISO(event.date);
          if (!isValid(eventDate)) return false;
          
          // Parse event time
          const [hours, minutes] = event.endTime.split(':').map(Number);
          if (isNaN(hours) || isNaN(minutes)) return false;
          
          // Set the event end time
          eventDate.setHours(hours, minutes, 0, 0);
          
          // Keep event if it ends after current time
          return eventDate > now;
        } catch (e) {
          console.error('Error parsing event date:', e);
          return false;
        }
      });

      futureEvents.sort((a, b) => {
        try {
          const dateA = parseISO(a.date);
          const dateB = parseISO(b.date);
          if (!isValid(dateA) || !isValid(dateB)) return 0;
          return dateA.getTime() - dateB.getTime();
        } catch (e) {
          console.error('Error sorting dates:', e);
          return 0;
        }
      });
      
      setEvents(futureEvents);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch events');
      logger.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [facilityId, user, businessId]);

  // After both events and recurring events are loaded, set loading to false
  useEffect(() => {
    if (facilityId && businessId) {
      // Start loading
      setLoading(true);
      
      // Fetch both types of events
      Promise.all([
        fetchEvents(),
        fetchRecurringEvents()
      ]).finally(() => {
        // Set loading to false once fetching is complete
        setLoading(false);
      });
    }
  }, [facilityId, fetchEvents, fetchRecurringEvents, businessId]);

  const allEvents = useMemo(() => [...events, ...recurringEventCards], [events, recurringEventCards]);

  const handleSignUp = async (eventId: string) => {
    if (!user || !facilityId) {
      setNotification({ message: 'Please log in to sign up for events.', severity: 'error' });
      return;
    }

    try {
      const event = allEvents.find(e => e.id === eventId);
      if (!event) {
        setNotification({ message: 'Event not found.', severity: 'error' });
        return;
      }

      if (event.participants && event.participants.length >= (event.capacity || 0)) {
        setNotification({ message: 'This event is already full.', severity: 'error' });
        return;
      }

      if (event.isCurrentUserSignedUp) {
        setNotification({ message: 'You are already signed up for this event.', severity: 'error' });
        return;
      }

      // If event has a price, fetch latest credit balance and show payment dialog
      if (event.price && event.price > 0) {
        try {
          const data = await eventRegistrationApi.getMemberCredits(user.uid, businessId, facilityId);
          setCreditBalance(data.balance);
        } catch (err) {
          console.error('Error fetching credit balance:', err);
        }
        setShowPaymentDialog({ open: true, event });
        return;
      }

      // If no price, proceed with direct registration
      const participant: ApiEventParticipant = {
        userId: user.uid,
        signUpDate: new Date().toISOString()
      };

      let response;
      if (event.isRecurring && event.recurringEventId) {
        response = await eventApi.addRecurringEventParticipant(businessId, facilityId, event.recurringEventId, participant);
      } else {
        response = await eventApi.addEventParticipant(businessId, facilityId, eventId, participant);
      }

      await fetchEvents();
      await fetchRecurringEvents();
      setNotification({ message: response.message, severity: 'success' });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to sign up for event');
      logger.error('Error signing up for event:', error);
      setNotification({ message: 'Failed to sign up for event. Please try again.', severity: 'error' });
    }
  };

  // Update the handlePayment function
  const handlePayment = async () => {
    if (!user || !facilityId || !showPaymentDialog.event) return;

    try {
      if (paymentMethod === 'credits') {
        if (creditBalance >= (showPaymentDialog.event.price || 0)) {
          // Full credit payment
          const data = await eventRegistrationApi.registerWithCredits(
            businessId,
            facilityId,
            showPaymentDialog.event.id,
            user.uid
          );
          setNotification({ 
            message: `Successfully registered with ${data.creditsUsed} credits! New balance: $${data.newBalance}`, 
            severity: 'success' 
          });
          setShowPaymentDialog({ open: false, event: null });
          await fetchEvents();
          await fetchRecurringEvents();
        } else if (creditBalance > 0) {
          // Mixed payment - first use available credits, then card for remainder
          const remainingAmount = (showPaymentDialog.event.price || 0) - creditBalance;
          console.log('Credit balance:', creditBalance);
          console.log('Event price:', showPaymentDialog.event.price);
          console.log('Remaining amount:', remainingAmount);
          
          const request = {
            memberId: user.uid,
            successUrl: `${window.location.origin}/member/dashboard/events?session_id={CHECKOUT_SESSION_ID}&event_id=${showPaymentDialog.event.id}`,
            cancelUrl: `${window.location.origin}/member/dashboard/events`,
            useCredits: true,
            creditAmount: creditBalance // Use the full available credit balance
          };

          console.log('Sending request:', request);
          const { url, creditsUsed, newBalance } = await eventApi.createEventCheckout(businessId, facilityId, showPaymentDialog.event.id, request);
          console.log('Response:', { url, creditsUsed, newBalance });
          
          // Show notification about credit usage before redirecting
          setNotification({ 
            message: `Using ${creditsUsed} credits. Remaining balance: $${newBalance}. Redirecting to payment for remaining $${remainingAmount}...`, 
            severity: 'info' 
          });
          
          // Small delay to show the notification before redirect
          setTimeout(() => {
            window.location.href = url;
          }, 2000);
        } else {
          // No credits available, proceed with full card payment
          const request = {
            memberId: user.uid,
            successUrl: `${window.location.origin}/member/dashboard/events?session_id={CHECKOUT_SESSION_ID}&event_id=${showPaymentDialog.event.id}`,
            cancelUrl: `${window.location.origin}/member/dashboard/events`,
            useCredits: false,
            creditAmount: 0
          };

          const { url } = await eventApi.createEventCheckout(businessId, facilityId, showPaymentDialog.event.id, request);
          window.location.href = url;
        }
      } else {
        // Full card payment
        const request = {
          memberId: user.uid,
          successUrl: `${window.location.origin}/member/dashboard/events?session_id={CHECKOUT_SESSION_ID}&event_id=${showPaymentDialog.event.id}`,
          cancelUrl: `${window.location.origin}/member/dashboard/events`,
          useCredits: false,
          creditAmount: 0
        };

        const { url } = await eventApi.createEventCheckout(businessId, facilityId, showPaymentDialog.event.id, request);
        window.location.href = url;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to process payment');
      logger.error('Error processing payment:', error);
      setNotification({ 
        message: error.message || 'Failed to process payment. Please try again.', 
        severity: 'error' 
      });
    }
  };

  // Add useEffect to handle checkout session completion
  useEffect(() => {
    const handleCheckoutCompletion = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const success = urlParams.get('success');
      
      if (sessionId) {
        try {
          // Find the event ID from the URL or state
          const eventId = urlParams.get('event_id');
          if (!eventId) {
            setNotification({ message: 'Event information not found.', severity: 'error' });
            return;
          }

          // Check registration status
          const { status, paymentStatus } = await eventApi.getEventRegistrationStatus(eventId, sessionId);
          
          if (status === 'registered' && paymentStatus === 'paid') {
            setNotification({ message: 'Successfully registered for the event!', severity: 'success' });
            // Refresh events to show updated registration
            await fetchEvents();
            await fetchRecurringEvents();
            // Refresh credit balance
            if (user && facilityId) {
              const data = await eventRegistrationApi.getMemberCredits(user.uid, businessId, facilityId);
              setCreditBalance(data.balance);
            }
          } else if (success === 'false') {
            setNotification({ message: 'Payment was cancelled.', severity: 'info' });
          } else {
            setNotification({ message: 'Payment or registration failed. Please try again.', severity: 'error' });
          }

          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
          logger.error('Error checking registration status:', err instanceof Error ? err : new Error(String(err)));
          setNotification({ message: 'Failed to verify registration. Please contact support.', severity: 'error' });
        }
      } else {
        // Refresh events when component mounts or URL changes without session_id
        // This ensures we have fresh data when returning to the page
        if (facilityId) {
          fetchEvents();
          fetchRecurringEvents();
        }
      }
    };

    // Only run if there's a session_id in the URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      handleCheckoutCompletion();
    }
  }, [user, facilityId, fetchEvents, fetchRecurringEvents, businessId]);

  const handleUnsignup = async (eventId: string) => {
    if (!user || !facilityId) {
      setNotification({ message: 'Please log in to manage your event registrations.', severity: 'error' });
      return;
    }

    try {
      const event = allEvents.find(e => e.id === eventId);
      if (!event || !event.participants) {
        setNotification({ message: 'Event not found.', severity: 'error' });
        return;
      }

      const userParticipation = event.participants.find(p => p.userId === user.uid);
      if (!userParticipation?.participantId) {
        setNotification({ message: 'You are not signed up for this event.', severity: 'error' });
        return;
      }

      let response;
      if (event.isRecurring && event.recurringEventId) {
        response = await recurringEventApi.removeRecurringEventParticipant(businessId, facilityId, event.recurringEventId, userParticipation.participantId);
      } else {
        response = await eventApi.removeEventParticipant(businessId, facilityId, eventId, userParticipation.participantId);
      }

      await fetchEvents();
      await fetchRecurringEvents();
      setConfirmUnsignup(null);
      setNotification({ message: response.message, severity: 'success' });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to remove from event');
      logger.error('Error removing from event:', error);
      setNotification({ message: 'Failed to remove from event. Please try again.', severity: 'error' });
    }
  };

  const renderEventCard = (event: EventData) => (
    <div key={event.id} className="event-card">
      <div className="event-card-content">
        <div className="event-header">
          <div>
            <h2 className="event-title">{event.title}</h2>
            {event.isRecurring && (
              <div className="event-recurring-badge">
                <RepeatIcon fontSize="small" />
                <span>Recurring</span>
              </div>
            )}
          </div>
          <span className={`event-status event-status-${event.status}`}>
            {event.status}
          </span>
        </div>

        <div className="event-meta">
          <div className="event-meta-item">
            <EventIcon className="event-meta-icon" />
            <span>{formatDate(event.date)}</span>
          </div>

          <div className="event-meta-item">
            <AccessTimeIcon className="event-meta-icon" />
            <span>{formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
          </div>

          <div className="event-meta-item">
            <GroupIcon className="event-meta-icon" />
            <span>{event.participants?.length || 0} / {event.capacity || 0} participants</span>
          </div>

          {event.price !== undefined && (
            <div className="event-meta-item">
              <span className="event-price">{event.price > 0 ? `$${event.price.toFixed(2)}` : 'Free'}</span>
            </div>
          )}
        </div>

        {event.description && (
          <p className="event-description">{event.description}</p>
        )}

        {event.isRecurring && event.recurrenceRule && (
          <div className="event-recurring-details">
            <div className="recurring-info">
              <RepeatIcon className="recurring-icon" />
              <span>
                {event.recurrenceRule.frequency === 'daily' && 'Daily'}
                {event.recurrenceRule.frequency === 'weekly' && 'Weekly'}
                {event.recurrenceRule.frequency === 'monthly' && 'Monthly'}
                {event.recurrenceRule.interval && event.recurrenceRule.interval > 1 && 
                  ` (every ${event.recurrenceRule.interval} ${event.recurrenceRule.frequency.slice(0, -2)}s)`}
              </span>
            </div>
            {event.recurrenceRule.endDate && (
              <div className="recurring-end-date">
                <span>Until {formatDate(event.recurrenceRule.endDate)}</span>
              </div>
            )}
          </div>
        )}

        {event.participants && event.participants.length > 0 && (
          <div className="event-participants">
            <div className="participants-header">
              <PersonIcon fontSize="small" />
              <span>Signed Up</span>
            </div>
            <div className="participants-grid">
              {event.participants.map((participant) => (
                <span
                  key={participant.userId}
                  className={`participant-chip ${
                    participant.userId === user?.uid ? 'participant-chip-current' : ''
                  }`}
                >
                  {participant.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="event-actions">
        {event.status === 'scheduled' && !event.isCurrentUserSignedUp && 
          (event.participants?.length || 0) < (event.capacity || 0) && (
          <button
            className="btn btn-signup"
            onClick={() => handleSignUp(event.id)}
          >
            {event.price && event.price > 0 ? 'Register & Pay' : 'Sign Up'}
          </button>
        )}
        {event.status === 'scheduled' && event.isCurrentUserSignedUp && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                fontSize: '13px',
                fontStyle: 'italic'
              }}
            >
              Cancellations are not available less than 24 hours before event
            </Typography>
            <EventRefund
              event={event}
              participantId={event.participants?.find(p => p.userId === user?.uid)?.participantId || ''}
              facilityId={facilityId || ''}
              businessId={businessId || ''}
              onSuccess={() => {
                fetchEvents();
                fetchRecurringEvents();
              }}
            />
          </div>
        )}
        {event.status === 'scheduled' && !event.isCurrentUserSignedUp && 
          (event.participants?.length || 0) >= (event.capacity || 0) && (
          <button
            className="btn"
            disabled
          >
            Event Full
          </button>
        )}
      </div>
    </div>
  );

  const getEventEndDateTime = (event: EventData) => {
    try {
      const date = parseISO(event.date);
      if (!isValid(date)) return null;
  
      const [hours, minutes] = event.endTime.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return null;
  
      const dateTime = new Date(date);
      dateTime.setHours(hours, minutes, 0, 0);
      
      return dateTime;
    } catch (error) {
      console.error('Error parsing event date/time:', error);
      return null;
    }
  };

  const now = new Date();
  
  // Filter upcoming events (excluding blocked events)
  const upcomingEvents = events
    .filter(event => !event.isBlocked && !event.isRecurring)
    .map(event => ({ ...event, endDateTime: getEventEndDateTime(event) }))
    .filter(event => event.endDateTime && event.endDateTime >= now)
    .sort((a, b) => (a.endDateTime?.getTime() || 0) - (b.endDateTime?.getTime() || 0));

  // Filter blocked events
  const blockedEvents = events
    .filter(event => event.isBlocked && !event.isRecurring)
    .map(event => ({ ...event, endDateTime: getEventEndDateTime(event) }))
    .sort((a, b) => (a.endDateTime?.getTime() || 0) - (b.endDateTime?.getTime() || 0));

  if (loading) {
    return <LoadingSpinner />;
  }

  return facilityId ? (
    <Box 
      sx={{ 
        minHeight: '100vh',
        backgroundColor: '#f6f8fa',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.1)',
          py: 5,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontSize: '28px',
                    fontWeight: 600,
                    color: '#1a1f36',
                    mb: 1,
                  }}
                >
                  Events and Classes
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#4f566b',
                    fontSize: '16px',
                    maxWidth: '600px',
                  }}
                >
                  View and manage your event registrations. Sign up for upcoming events and classes at your facility.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              {user && facilityId && (
                <CreditBalance memberId={user.uid} businessId={businessId} facilityId={facilityId} />
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                minWidth: 120,
              },
            }}
          >
            <Tab label="Recurring Events" />
            <Tab label="Upcoming Events" />
            <Tab label="Blocked Dates" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '16px',
                fontWeight: 600,
                color: '#1a1f36',
                mb: 3,
              }}
            >
              Recurring Events
            </Typography>
            {recurringEventsLoading ? (
              <LoadingSpinner />
            ) : recurringEventCards.length > 0 ? (
              <Stack spacing={2}>
                {recurringEventCards.map(renderEventCard)}
              </Stack>
            ) : (
              <EmptyState message="No recurring events scheduled" />
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '16px',
                fontWeight: 600,
                color: '#1a1f36',
                mb: 3,
              }}
            >
              Upcoming Events
            </Typography>
            {upcomingEvents.length > 0 ? (
              <Stack spacing={2}>
                {upcomingEvents.map(renderEventCard)}
              </Stack>
            ) : (
              <EmptyState message="No upcoming events scheduled" />
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '16px',
                fontWeight: 600,
                color: '#1a1f36',
                mb: 3,
              }}
            >
              Blocked Time
            </Typography>
            {blockedEvents.length > 0 ? (
              <Stack spacing={2}>
                {blockedEvents.map(renderEventCard)}
              </Stack>
            ) : (
              <EmptyState message="No blocked time" />
            )}
          </Box>
        )}

        <Dialog
          open={!!confirmUnsignup?.open}
          onClose={() => setConfirmUnsignup(null)}
          maxWidth="sm"
          fullWidth
          classes={{ paper: 'dialog' }}
        >
          <DialogTitle className="dialog-title">Cancel Event Registration</DialogTitle>
          <DialogContent className="dialog-content">
            <Typography>
              Are you sure you want to cancel your registration for this event?
            </Typography>
          </DialogContent>
          <DialogActions className="dialog-actions">
            <Button 
              onClick={() => setConfirmUnsignup(null)}
              className="btn"
            >
              Keep Registration
            </Button>
            <Button 
              onClick={() => confirmUnsignup?.eventId && handleUnsignup(confirmUnsignup.eventId)}
              className="btn btn-cancel"
            >
              Cancel Registration
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Payment Dialog */}
        <Dialog
          open={showPaymentDialog.open}
          onClose={() => setShowPaymentDialog({ open: false, event: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Complete Registration</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Event: {showPaymentDialog.event?.title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              Price: ${showPaymentDialog.event?.price}
            </Typography>

            {creditBalance > 0 && (
              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <FormLabel component="legend">Payment Method</FormLabel>
                <RadioGroup
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as 'card' | 'credits')}
                >
                  <FormControlLabel
                    value="card"
                    control={<Radio />}
                    label="Credit Card"
                  />
                  <FormControlLabel
                    value="credits"
                    control={<Radio />}
                    label={`Credits (Balance: $${creditBalance.toFixed(2)})`}
                  />
                </RadioGroup>
              </FormControl>
            )}

            {paymentMethod === 'credits' && creditBalance < (showPaymentDialog.event?.price || 0) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Insufficient Credits</AlertTitle>
                You don't have enough credits to pay for this event.
                {creditBalance > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    You can use ${creditBalance.toFixed(2)} in credits and pay the remaining ${((showPaymentDialog.event?.price || 0) - creditBalance).toFixed(2)} with a credit card.
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  You can earn credits by canceling event registrations at least 24 hours before the event.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPaymentDialog({ open: false, event: null })}>
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              variant="contained"
              color="primary"
              disabled={paymentMethod === 'credits' && creditBalance <= 0}
            >
              {paymentMethod === 'credits' 
                ? creditBalance >= (showPaymentDialog.event?.price || 0)
                  ? 'Pay with Credits'
                  : `Pay $${((showPaymentDialog.event?.price || 0) - creditBalance).toFixed(2)} with Card`
                : 'Proceed to Payment'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification(null)} 
          severity={notification?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  ) : (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-red-600">Failed to load facility information</div>
    </div>
  );
};

export default MemberEvents;


